import {
  captureException,
  init,
  replayIntegration,
  tanstackRouterBrowserTracingIntegration,
} from "@sentry/react";
import { isLocal } from "@/config";
import { showFallbackErrorUI } from "./fallback-error-ui";

type TanStackRouter = Parameters<
  typeof tanstackRouterBrowserTracingIntegration
>[0];

let sentryInitialized = false;

/**
 * Initializes Sentry with TanStack Router tracing, replay, and global error handlers.
 * @param router - TanStack Router instance used for navigation performance spans.
 */
export default function initializeLogging(router: TanStackRouter) {
  if (isLocal) return;

  if (sentryInitialized) {
    return;
  }
  const appUrl = import.meta.env.VITE_APP_URL;
  const apiUrl = import.meta.env.VITE_API_URL;
  const wsApiUrl = import.meta.env.VITE_WEBSOCKET_URL;
  const dsn =
    "https://9885120d38efdc6218ce45aad9f01bf2@o1291819.ingest.sentry.io/4506488575688704";

  init({
    dsn,
    debug: isLocal,
    //TODO add release with release and source maps
    environment: import.meta.env.VITE_ENVIRONMENT,
    integrations: [
      tanstackRouterBrowserTracingIntegration(router),
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracePropagationTargets: [
      appUrl,
      new RegExp(`^${apiUrl}/api`),
      new RegExp(`^${apiUrl}/graphql/`),
      new RegExp(`^${wsApiUrl}/graphql/`),
    ],
    profilesSampleRate: 1,
    tracesSampleRate: 1,
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
    beforeSend(event, hint) {
      const error = hint.originalException || event.exception?.values?.[0];
      const errorMessage =
        (error && typeof error === "object" && "value" in error
          ? String(error.value)
          : null) ||
        (error && typeof error === "object" && "message" in error
          ? String(error.message)
          : null) ||
        event.message ||
        "";

      const reactErrorMatch =
        errorMessage.match(/React error #(\d+)/i) ||
        errorMessage.match(/Minified React error #(\d+)/i);

      if (reactErrorMatch) {
        const reactErrorCode = reactErrorMatch[1];
        return {
          ...event,
          tags: {
            ...event.tags,
            errorType: "react-error",
            reactErrorCode,
          },
          extra: {
            ...event.extra,
            isReactError: true,
            reactErrorCode,
            hint: `React error #${reactErrorCode}. Visit https://react.dev/errors/${reactErrorCode} for more information.`,
          },
        };
      }

      return event;
    },
  });

  sentryInitialized = true;

  window.addEventListener("error", (event) => {
    const errorMessage = event.error?.message || event.message || "";
    const errorStack = event.error?.stack || "";

    const reactErrorMatch =
      errorMessage.match(/React error #(\d+)/i) ||
      errorMessage.match(/Minified React error #(\d+)/i) ||
      errorStack.match(/React error #(\d+)/i);

    if (reactErrorMatch) {
      const reactErrorCode = reactErrorMatch[1];
      console.warn(
        `[Global Error Handler] Caught React error #${reactErrorCode} that bypassed error boundary`,
      );
      captureException(event.error || new Error(errorMessage), {
        level: "error",
        tags: {
          errorType: "react-error",
          reactErrorCode,
          caughtBy: "global-error-handler",
        },
        extra: {
          message: errorMessage,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: errorStack,
          reactErrorCode,
          reactErrorUrl: `https://react.dev/errors/${reactErrorCode}`,
          note: "This error bypassed React error boundary and was caught by global error handler",
        },
      });
      showFallbackErrorUI(reactErrorCode);
    } else {
      captureException(event.error || new Error(errorMessage), {
        level: "error",
        extra: {
          message: errorMessage,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: errorStack,
        },
      });
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;
    const errorMessage =
      error?.message || String(error) || "Unhandled promise rejection";
    const errorStack = error?.stack || "";

    const reactErrorMatch =
      errorMessage.match(/React error #(\d+)/i) ||
      errorMessage.match(/Minified React error #(\d+)/i) ||
      errorStack.match(/React error #(\d+)/i);

    if (reactErrorMatch) {
      const reactErrorCode = reactErrorMatch[1];
      console.warn(
        `[Global Error Handler] Caught React error #${reactErrorCode} in promise rejection that bypassed error boundary`,
      );
      captureException(
        error instanceof Error ? error : new Error(errorMessage),
        {
          level: "error",
          tags: {
            errorType: "react-error",
            reactErrorCode,
            caughtBy: "global-error-handler",
          },
          extra: {
            message: errorMessage,
            stack: errorStack,
            reactErrorCode,
            reactErrorUrl: `https://react.dev/errors/${reactErrorCode}`,
            note: "This error bypassed React error boundary and was caught by global error handler (promise rejection)",
          },
        },
      );
      showFallbackErrorUI(reactErrorCode);
    } else {
      captureException(
        error instanceof Error ? error : new Error(errorMessage),
        {
          level: "error",
          extra: {
            message: errorMessage,
            stack: errorStack,
          },
        },
      );
    }
  });
}
